import moment from "moment";

// Shapes the two TDLObservation feeds into what the "Results overview" tab
// draws: a date × test matrix, one series per test for the graph tiles, and a
// reading of every value against the reference range the lab sent with it.
//
// Nothing here supplies a range of its own. Every verdict comes from the
// result row: the HL7 abnormal flag first, then the reference range.

const NUMBER = "(-?\\d+(?:\\.\\d+)?)";

// A result value as a number, when it is one.
//
// "5.1" and "<0.3" are numbers (the second with a qualifier). "52.9% 1.99" is
// a differential count - the percentage, then the absolute count - and the
// units and range on the row describe the absolute count, so that is the one
// used. "Negative" and "Not Detected" are not numbers.
export function numericOf(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  let m = new RegExp(`^([<>≤≥]=?)?\\s*${NUMBER}$`).exec(s);
  if (m) return { value: Number(m[2]), qualifier: m[1] || "" };
  m = new RegExp(`^${NUMBER}\\s*%\\s+${NUMBER}$`).exec(s);
  if (m) return { value: Number(m[2]), qualifier: "", percent: Number(m[1]) };
  return null;
}

// Reference ranges are free text: "115 - 155", "<5", ">10". Anything else
// (a qualitative range such as "Negative") has no numbers to compare against.
export function parseRange(text) {
  const s = String(text ?? "")
    .trim()
    .replace(/[–—]/g, "-")
    .replace(/^\((.*)\)$/, "$1")
    .trim();
  if (!s) return null;
  let m = new RegExp(`^${NUMBER}\\s*-\\s*${NUMBER}$`).exec(s);
  if (m) return { lo: Number(m[1]), hi: Number(m[2]) };
  m = new RegExp(`^(?:<=?|≤)\\s*${NUMBER}$`).exec(s);
  if (m) return { lo: null, hi: Number(m[1]) };
  m = new RegExp(`^(?:>=?|≥)\\s*${NUMBER}$`).exec(s);
  if (m) return { lo: Number(m[1]), hi: null };
  return null;
}

// HL7 OBX-8 abnormal flags.
const FLAG_LEVELS = {
  H: "high",
  HH: "high",
  ">": "high",
  L: "low",
  LL: "low",
  "<": "low",
  A: "abnormal",
  AA: "abnormal",
};

export function assessResult({ value, range, flag }) {
  const f = String(flag ?? "").trim().toUpperCase();
  if (f && f !== "N") {
    return { level: FLAG_LEVELS[f] || "abnormal", reason: `Flagged by the lab (${f})` };
  }

  const num = numericOf(value);
  const r = parseRange(range);
  if (num && r) {
    // "<0.3" against an upper limit of 0.2 could be either side of it, so a
    // qualified value is only called when the qualifier cannot change the answer.
    if (r.hi != null && num.value > r.hi) {
      if (num.qualifier.startsWith("<")) return { level: "unknown", reason: `Compare with the range ${range}` };
      return { level: "high", reason: `Above the reference range (${range})` };
    }
    if (r.lo != null && num.value < r.lo) {
      if (num.qualifier.startsWith(">")) return { level: "unknown", reason: `Compare with the range ${range}` };
      return { level: "low", reason: `Below the reference range (${range})` };
    }
    return { level: "normal", reason: `Within the reference range (${range})` };
  }

  if (f === "N") return { level: "normal", reason: "Marked normal by the lab" };

  const v = String(value ?? "").trim().toLowerCase();
  if (range && v && v === String(range).trim().toLowerCase()) {
    return { level: "normal", reason: "Matches the expected result" };
  }
  return {
    level: "unknown",
    reason: range ? `Compare with the reference range (${range})` : "No reference range on this result",
  };
}

export function needsAttention(cell) {
  const level = cell?.assessment?.level;
  return level === "high" || level === "low" || level === "abnormal";
}

// One row per day, one column per test. Where a test was reported twice on
// the same day, the later report wins - the same "Last" the pivot tab uses.
export function buildObservationMatrix(results) {
  const rowsByDay = new Map();
  const tests = new Map();

  for (const r of results || []) {
    const name = String(r?.text || r?.identifier || "").trim();
    if (!name) continue;
    const when = moment(r.dateTimeofTransaction);
    if (!when.isValid()) continue;

    const cell = {
      value: String(r.observationValue ?? "").trim(),
      units: r.units || "",
      range: r.referenceRange || "",
      flag: r.abnormalFlags || "",
      code: r.identifier || "",
      ts: when.valueOf(),
    };
    cell.assessment = assessResult(cell);

    const key = when.format("YYYY-MM-DD");
    let row = rowsByDay.get(key);
    if (!row) {
      row = {
        key,
        date: when.format("DD-MMM-YYYY").toUpperCase(),
        ts: when.clone().startOf("day").valueOf(),
        values: {},
      };
      rowsByDay.set(key, row);
    }
    const previous = row.values[name];
    if (!previous || previous.ts <= cell.ts) row.values[name] = cell;

    const test = tests.get(name) || { name, unit: "", code: "", lastTs: -Infinity };
    if (cell.ts >= test.lastTs) {
      if (cell.units) test.unit = cell.units;
      if (cell.code) test.code = cell.code;
      test.lastTs = cell.ts;
    }
    tests.set(name, test);
  }

  return {
    tests: [...tests.values()].sort((a, b) => a.name.localeCompare(b.name)),
    rows: [...rowsByDay.values()].sort((a, b) => b.ts - a.ts),
  };
}

// One series per test, oldest first. A test is graphed when every value is a
// number and at least one is a plain number - a column of "<0.3" has nothing
// to draw and reads better as the dated list a text tile gives it.
export function buildTestSeries(matrix) {
  const oldestFirst = [...matrix.rows].reverse();
  return matrix.tests
    .map((test) => {
      const points = oldestFirst
        .map((row) => {
          const cell = row.values[test.name];
          if (!cell || cell.value === "") return null;
          return { ts: row.ts, date: row.date, cell, num: numericOf(cell.value) };
        })
        .filter(Boolean);
      const graphable =
        points.length > 0 && points.every((p) => p.num) && points.some((p) => !p.num.qualifier);
      return { name: test.name, unit: test.unit, code: test.code, kind: graphable ? "graph" : "text", points };
    })
    .filter((s) => s.points.length > 0);
}
